import { Component } from 'react';
import type { ErrorInfo, ReactNode } from 'react';

/**
 * What the customer sees when a screen throws.
 *
 * Without one of these, a single bad render unmounts the whole application and
 * leaves a white page — no navigation, no sign-out, no way to reach the screen
 * that still works. That is the worst possible outcome from the cheapest
 * possible bug, and the customer's only recourse is to guess that reloading
 * might help.
 *
 * So the frame stays and only the content is replaced. Two deliberate choices
 * in what it says:
 *
 *  - **It does not show the error.** A stack trace names our files and our
 *    internals, and means nothing to somebody who wanted to look at their
 *    panel. It goes to the console, where a developer looking for it will
 *    find it.
 *  - **It offers the two things that actually work**: try this screen again,
 *    which remounts the subtree, and go home, which is a different screen. A
 *    lone "reload the page" makes the customer do the work of deciding.
 *
 * A class, because this is the one thing React has no hook for.
 */
export class ErrorBoundary extends Component<
  {
    children: ReactNode;
    /** Rendered in place of the children; receives a way to try again. */
    fallback: (retry: () => void) => ReactNode;
    /** Changing this resets the boundary — used to clear it on navigation. */
    resetKey?: string;
  },
  { failed: boolean }
> {
  override state = { failed: false };

  static getDerivedStateFromError(): { failed: boolean } {
    return { failed: true };
  }

  override componentDidUpdate(previous: { resetKey?: string }): void {
    // Navigating away from a broken screen should not keep showing the error
    // on the screen the customer just asked for.
    if (this.state.failed && previous.resetKey !== this.props.resetKey) {
      this.setState({ failed: false });
    }
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // The one place this is useful, and the one place it is safe.
    console.error('a screen failed to render', error, info.componentStack);
  }

  override render(): ReactNode {
    if (!this.state.failed) return this.props.children;
    return this.props.fallback(() => this.setState({ failed: false }));
  }
}
